import {
  createElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import styles from "./EditableName.module.css";

export interface EditableNameProps {
  /** Current persisted name. The parent owns it; this component is dumb. */
  value: string;
  /** Fires on Enter or blur when the trimmed input is non-empty AND different
   *  from `value`. Esc, empty submissions, and unchanged submissions are silently
   *  dropped so the cinema aesthetic stays free of red banners on a stray
   *  backspace. */
  onSave: (next: string) => void;
  /** Mount in edit mode and auto-focus+select. Use for ?focus=name handoffs.
   *  Lazy-initialised: a parent toggling this back to false does NOT close
   *  the editor while the user is mid-rename. */
  initialEditing?: boolean;
  /** Tag rendered in static mode. Defaults to span. `as="h2"` for ScenePage. */
  as?: keyof JSX.IntrinsicElements;
  /** Optional class on both modes. */
  className?: string;
  /** Optional class on the input only. */
  inputClassName?: string;
  /** Optional aria-label override. */
  ariaLabel?: string;
  /** Optional inline style applied to both modes (e.g. width hints). */
  style?: CSSProperties;
  /** Placeholder shown in edit mode when draft is empty. */
  placeholder?: string;
  /** Max input length. Backend max for names is 200. */
  maxLength?: number;
}

export function EditableName({
  value,
  onSave,
  initialEditing,
  as = "span",
  className,
  inputClassName,
  ariaLabel,
  style,
  placeholder,
  maxLength = 200,
}: EditableNameProps) {
  // Lazy init so a parent flipping `initialEditing` from true to false (e.g.
  // after stripping ?focus=name) doesn't yank the user out of edit mid-rename.
  const [editing, setEditing] = useState<boolean>(() => !!initialEditing);
  const [draft, setDraft] = useState<string>(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const begin = useCallback(() => {
    setDraft(value);
    setEditing(true);
  }, [value]);

  const commit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== value) {
      onSave(trimmed);
    }
    setEditing(false);
  }, [draft, value, onSave]);

  const cancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  // When the input mounts, focus + select. Skipping `value` in deps is
  // deliberate: re-priming on external value changes mid-edit would clobber
  // the user's typing.
  useEffect(() => {
    if (!editing) return;
    const node = inputRef.current;
    if (!node) return;
    node.focus();
    node.select();
  }, [editing]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel],
  );

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className={[styles.input, className, inputClassName].filter(Boolean).join(" ")}
        style={style}
        aria-label={ariaLabel ?? "Rename"}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }

  return createElement(
    as,
    {
      role: "button",
      tabIndex: 0,
      className: [styles.static, className].filter(Boolean).join(" "),
      style,
      "aria-label": ariaLabel ?? `${value} — click to rename`,
      onClick: begin,
      onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          begin();
        }
      },
    },
    value,
  );
}
